/**
 * @module MoveProjectModal
 * @description A modal component for moving a project to a new owner.
 */

import React, { useEffect, useState } from "react"
import { Modal, List, Checkbox } from "antd"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExchangeAlt } from "@fortawesome/free-solid-svg-icons"
import { useMoveProjectMutation } from "features/Projects/api/useProjectsMutations"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { useProjectStore } from "stores/projectStore"

interface MoveProjectModalProps {
  userData: string[]
  /** When given, the parent opens the modal (from a menu) and no button is shown */
  open?: boolean
  /** Told when the modal opens or closes, for a parent that controls `open` */
  onOpenChange?: (open: boolean) => void
}

/**
 * @function MoveProjectModal
 * @description A modal component for moving a project to a new owner.
 * @param props - The component props
 * @param props.userData - Array of available users in the unit
 * @returns The MoveProjectModal component
 */
const MoveProjectModal: React.FC<MoveProjectModalProps> = ({
  userData,
  open,
  onOpenChange,
}) => {
  const project = useProjectStore((state) => state.project)
  const moveProjectMutation = useMoveProjectMutation()
  const navigate = useNavigate()

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  // A parent that passes `open` owns the visibility; otherwise this does.
  const isVisible = open ?? isModalVisible
  const setVisible = (visible: boolean): void => {
    setIsModalVisible(visible)
    onOpenChange?.(visible)
  }
  const [displayUserData, setDisplayUserData] = useState<string[]>([])
  const [selectedOwner, setSelectedOwner] = useState<string>("")

  // Filter out the current project creator from the list
  useEffect(() => {
    if (userData.length > 0) {
      const userArray = userData.filter(
        (user: string) =>
          user.toLowerCase() !== project.Created_by.toLowerCase()
      )
      setDisplayUserData(userArray)
    }
  }, [userData, project.Created_by])

  /**
   * @async
   * @function moveProject
   * @description Moves the project to a new owner.
   * @returns {Promise<void>}
   */
  const moveProject = async (): Promise<void> => {
    if (!selectedOwner) {
      notify.error("Error Moving Project!", "Please select a new owner.")
      return
    }

    try {
      await moveProjectMutation.mutateAsync({
        id: project.id,
        owner: project.Created_by,
        newOwner: selectedOwner,
      })
      navigate("/home")
    } catch (error) {
      // Notifications are handled by the mutation; just log.
      console.error("Error moving project:", error)
    }
  }

  /**
   * @function showModal
   * @description Sets the modal visibility to true.
   */
  const showModal = (): void => {
    setVisible(true)
  }

  /**
   * @function cancel
   * @description Resets the modal state and closes it.
   */
  const cancel = (): void => {
    setVisible(false)
    setSelectedOwner("")
  }

  /**
   * @function handleOwnerSelect
   * @description Handles the selection of a new owner.
   * @param {string} user - The selected user
   * @returns {Function} The event handler function
   */
  const handleOwnerSelect = (user: string) => (): void => {
    setSelectedOwner(user === selectedOwner ? "" : user)
  }

  /**
   * Handles checkbox click events
   * @param {React.MouseEvent} e - The click event
   * @param {string} user - The user name
   */
  const handleCheckboxClick = (e: React.MouseEvent, user: string): void => {
    e.stopPropagation()
    handleOwnerSelect(user)()
  }

  return (
    <>
      {open === undefined && (
        <DataQButton onClick={showModal} type="primary">
          Move Project
          <FontAwesomeIcon icon={faExchangeAlt} />
        </DataQButton>
      )}

      <Modal
        open={isVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
        width={600}
      >
        <h2 className="text-xl font-semibold m-0 mb-2">Select New Owner</h2>

        <List
          itemLayout="horizontal"
          dataSource={displayUserData}
          renderItem={(user: string) => (
            <List.Item
              onClick={handleOwnerSelect(user)}
              className="cursor-pointer hover:bg-gray-200 rounded-2xl"
              actions={[
                <Checkbox
                  key={user}
                  className="mr-2"
                  onClick={(e: React.MouseEvent) =>
                    handleCheckboxClick(e, user)
                  }
                  checked={selectedOwner === user}
                />,
              ]}
            >
              <List.Item.Meta className="p-2 ml-2" title={user} />
            </List.Item>
          )}
        />

        <div className="flex flex-row mt-5 justify-between">
          <DataQButton type="secondary" onClick={cancel}>
            Cancel
          </DataQButton>
          <DataQButton
            type="primary"
            onClick={moveProject}
            isLoading={moveProjectMutation.isPending}
            isDisabled={moveProjectMutation.isPending || !selectedOwner}
          >
            Move Project
            {selectedOwner && <span className="ml-1">to {selectedOwner}</span>}
          </DataQButton>
        </div>
      </Modal>
    </>
  )
}

export default MoveProjectModal
